/**
 * Shared SEO text and bundle helpers (Milestone 14).
 */
import {
  AUTOMAPA_DEFAULT_DESCRIPTION,
  AUTOMAPA_DEFAULT_OG_IMAGE,
  AUTOMAPA_DEFAULT_TITLE,
  AUTOMAPA_LOCALE,
  AUTOMAPA_OG_TYPE,
  AUTOMAPA_SITE_NAME,
  DESCRIPTION_MAX_LENGTH,
  TITLE_MAX_LENGTH,
} from "./seoConstants";
import { buildAbsoluteUrl, resolveOgImageUrl } from "./canonicalUrls";

const HTML_TAG_RE = /<[^>]*>/g;
const INTERNAL_ID_RE = /\b(listing|dealer|vehicle)[-_]?id\s*[:=]\s*\S+/gi;

export function sanitizeSeoText(value, { maxLength = null } = {}) {
  if (value == null) return "";
  let text = String(value)
    .replace(HTML_TAG_RE, " ")
    .replace(INTERNAL_ID_RE, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (/^(undefined|null)$/i.test(text)) return "";

  if (maxLength != null && text.length > maxLength) {
    const slice = text.slice(0, maxLength);
    const lastSpace = slice.lastIndexOf(" ");
    text = (lastSpace > maxLength * 0.6 ? slice.slice(0, lastSpace) : slice).trim();
    if (!text.endsWith("…")) text += "…";
  }

  return text;
}

export function buildPageTitle(parts, { suffix = AUTOMAPA_SITE_NAME, maxLength = TITLE_MAX_LENGTH } = {}) {
  const segments = (Array.isArray(parts) ? parts : [parts])
    .map((part) => sanitizeSeoText(part))
    .filter(Boolean);

  const core = segments.join(" ");
  if (!core) return sanitizeSeoText(AUTOMAPA_DEFAULT_TITLE, { maxLength });

  const withSuffix = suffix ? `${core} | ${suffix}` : core;
  return sanitizeSeoText(withSuffix, { maxLength });
}

export function buildMetaDescription(text, { maxLength = DESCRIPTION_MAX_LENGTH, fallback = AUTOMAPA_DEFAULT_DESCRIPTION } = {}) {
  const cleaned = sanitizeSeoText(text, { maxLength });
  return cleaned || sanitizeSeoText(fallback, { maxLength });
}

export function buildOpenGraphBundle({
  title,
  description,
  image,
  url,
  type = AUTOMAPA_OG_TYPE,
  siteName = AUTOMAPA_SITE_NAME,
  locale = AUTOMAPA_LOCALE,
} = {}) {
  return {
    title: sanitizeSeoText(title),
    description: buildMetaDescription(description),
    image: resolveOgImageUrl(image),
    url: url || undefined,
    type,
    siteName,
    locale,
  };
}

export function buildTwitterBundle({ title, description, image, card = "summary_large_image" } = {}) {
  return {
    card,
    title: sanitizeSeoText(title),
    description: buildMetaDescription(description),
    image: resolveOgImageUrl(image),
  };
}

export function buildSeoBundle({
  title,
  description,
  canonicalPath,
  robots = "index, follow",
  image,
  ogType = AUTOMAPA_OG_TYPE,
} = {}) {
  const canonicalUrl = canonicalPath ? buildAbsoluteUrl(canonicalPath) : undefined;
  const resolvedTitle = buildPageTitle(title, { suffix: null }) || AUTOMAPA_DEFAULT_TITLE;
  const resolvedDescription = buildMetaDescription(description);
  const resolvedImage = resolveOgImageUrl(image);

  return {
    title: resolvedTitle.includes("|") ? resolvedTitle : buildPageTitle(resolvedTitle),
    description: resolvedDescription,
    canonicalUrl,
    canonicalPath,
    robots,
    openGraph: buildOpenGraphBundle({
      title: resolvedTitle,
      description: resolvedDescription,
      image: resolvedImage,
      url: canonicalUrl,
      type: ogType,
    }),
    twitter: buildTwitterBundle({
      title: resolvedTitle,
      description: resolvedDescription,
      image: resolvedImage,
    }),
  };
}

export function robotsDirective(shouldIndex, { follow = true } = {}) {
  return `${shouldIndex ? "index" : "noindex"}, ${follow ? "follow" : "nofollow"}`;
}
